import React from 'react';
import { Switch, Route, Redirect } from 'react-router-dom';
import { lazy, Suspense } from 'react';
import ImpersonationBanner from '../components/ImpersonationBanner';
import ArtistLayout from '../components/Artist/ArtistLayout';
import ArtistFlowHandler from './ArtistFlowHandler';

// Lazy load components for better performance
const ArtistOnboarding = lazy(() => import('pages/Onboarding/ArtistOnboarding'));
const ArtistDashboard = lazy(() => import('pages/Artist/ArtistDashboard'));
const MusicHubAlbumDetail = lazy(() => import('pages/Artist/MusicHubAlbumDetail'));
const MusicHubEditAlbum = lazy(() => import('pages/Artist/MusicHubEditAlbum'));
const MusicHubEditSong = lazy(() => import('pages/Artist/MusicHubEditSong'));
const BeneficiaryDashboard = lazy(() => import('pages/BeneficiaryDashboard'));
const UploadLanding = lazy(() => import('pages/Upload'));
const UploadSingleFlow = lazy(() => import('pages/Upload/UploadSingleFlow'));
const UploadAlbumWizard = lazy(() => import('pages/Upload/UploadAlbumWizard'));
// Affiliate UI lives in starfarm-admin; test page disabled
// const TestAffiliate = lazy(() => import('pages/TestAffiliate'));
const AnalyticsPage = lazy(() => import('pages/Analytics'));
const ProfilePage = lazy(() => import('pages/Artist/ProfilePage'));
const EarningsPage = lazy(() => import('pages/Earnings'));
const StripeSetupPage = lazy(() => import('./StripeSetup'));

const LoadingSpinner = () => (
  <div style={{
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    height: '100vh',
    backgroundColor: '#02172a',
    color: '#fff'
  }}>
    <div>Loading...</div>
  </div>
);

const ArtistRouter: React.FC = () => {
  return (
    <>
      <ImpersonationBanner />
      <ArtistLayout>
        <ArtistFlowHandler />
        <Suspense fallback={<LoadingSpinner />}>
          <Switch>
            {/* Onboarding */}
            <Route exact path="/artist/onboarding" component={ArtistOnboarding} />

            {/* Legacy full beneficiary dashboard (plays, earnings, Stripe) */}
            <Route exact path="/" component={BeneficiaryDashboard} />
            {/* Same shell as RoleDashboardRouter → ArtistRouter: streamers land on /dashboard */}
            <Route exact path="/dashboard" component={BeneficiaryDashboard} />
            {/* Legacy admin links used /beneficiary?token=&artistId= — same app, canonical URL is /dashboard */}
            <Route
              exact
              path="/beneficiary"
              render={({ location }) => (
                <Redirect to={{ pathname: '/dashboard', search: location.search }} />
              )}
            />

            {/* Hub: catalog + links to upload, profile, etc. */}
            <Route exact path="/artist/dashboard" component={ArtistDashboard} />
            <Route exact path="/artist/music/:albumId" component={MusicHubAlbumDetail} />
            <Route exact path="/artist/album/edit" component={MusicHubEditAlbum} />
            <Route exact path="/artist/song/edit" component={MusicHubEditSong} />

            <Route exact path="/upload" component={UploadLanding} />
            <Route exact path="/upload/single" component={UploadSingleFlow} />
            <Route exact path="/upload/album" component={UploadAlbumWizard} />

            <Route exact path="/profile" component={ProfilePage} />

            <Route exact path="/earnings" component={EarningsPage} />

            <Route exact path="/analytics" component={AnalyticsPage} />

            {/* Stripe onboarding flow */}
            <Route exact path="/stripe/setup" component={StripeSetupPage} />

            <Redirect to="/dashboard" />
          </Switch>
        </Suspense>
      </ArtistLayout>
    </>
  );
};

export default ArtistRouter;
